import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Building2,
  Loader2,
  ShieldCheck,
} from "lucide-react";
import Seo from "@/components/seo/Seo";
import { MOBILE_APP_API } from "@/lib/site";

const INITIAL_FORM = {
  company_name: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  employee_count: 100,
  package: "standard",
  notes: "",
  analytics_opt_in: true,
  website: "", // honeypot — real users never fill this
};

const FALLBACK_PACKAGES = [
  { id: "starter", label: "Starter", description: "3 months Pro" },
  { id: "standard", label: "Standard", description: "6 months Pro" },
  { id: "premium", label: "Premium", description: "12 months Pro" },
  { id: "enterprise", label: "Enterprise", description: "Custom quote" },
];

export default function ForEmployersInquiry() {
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [packages, setPackages] = useState(FALLBACK_PACKAGES);
  const [pricing, setPricing] = useState({}); // { [packageId]: {per_employee_display, total_display} }
  const [loading, setLoading] = useState(false);

  // Debounced live pricing every time employee count changes.
  useEffect(() => {
    if (!MOBILE_APP_API) return;
    const n = Number(form.employee_count);
    if (!Number.isFinite(n) || n < 1) return; // skip fetch while field is empty
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(
          `${MOBILE_APP_API}/api/corporate/packages`,
          { params: { employee_count: n }, timeout: 5000 },
        );
        if (cancelled) return;
        const list = Array.isArray(data?.packages) ? data.packages : [];
        if (list.length) {
          setPackages(list.map((p) => ({
            id: p.id,
            label: p.label || p.id,
            // Short, mobile-friendly label. Ignore the API's long marketing
            // description here — the pricing cards on the landing carry the
            // full copy. Dropdown just needs "N months Pro" or "Custom quote".
            description: p.is_custom
              ? "Custom quote"
              : p.months
                ? `${p.months} months Pro`
                : "",
          })));
          const px = {};
          for (const p of list) {
            px[p.id] = {
              per_employee_display: p.per_employee_display,
              total_display: p.total_display,
              is_custom: !!p.is_custom,
            };
          }
          setPricing(px);
        }
      } catch {
        // Silent — the form still works with hardcoded package IDs.
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [form.employee_count]);

  const selected = useMemo(
    () => packages.find((p) => p.id === form.package) || packages[1] || packages[0],
    [packages, form.package],
  );
  const selectedPrice = pricing[form.package];

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    // Honeypot — silently drop bot submissions.
    if (form.website) return;

    // Client-side validation matches the backend contract (2–120 / 2–80 / 1–100000).
    const co = form.company_name.trim();
    const nm = form.contact_name.trim();
    const em = form.contact_email.trim();
    const ec = Number(form.employee_count);
    if (
      co.length < 2 || co.length > 120 ||
      nm.length < 2 || nm.length > 80 ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em) ||
      !Number.isFinite(ec) || ec < 1 || ec > 100000
    ) {
      setError("Please double-check the required fields.");
      return;
    }

    if (!MOBILE_APP_API) {
      setError(
        "Signup is momentarily offline. Please email customersupport@atlasforgefinancial.com and we'll get you set up manually.",
      );
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        company_name: co,
        contact_name: nm,
        contact_email: em.toLowerCase(),
        contact_phone: form.contact_phone.trim() || null,
        employee_count: ec,
        package: form.package,
        notes: form.notes.trim() || null,
        analytics_opt_in: Boolean(form.analytics_opt_in),
        source_url: typeof window !== "undefined" ? window.location.href : "",
      };
      const { data } = await axios.post(
        `${MOBILE_APP_API}/api/corporate/inquiry`,
        payload,
        { timeout: 10000 },
      );
      const params = new URLSearchParams();
      if (data?.inquiry_id) params.set("id", data.inquiry_id);
      if (data?.estimated_total_display) {
        params.set("total", data.estimated_total_display);
      }
      if (data?.estimated_per_employee_display) {
        params.set("per_employee", data.estimated_per_employee_display);
      }
      if (data?.package) params.set("pkg", data.package);
      navigate(`/for-employers/thank-you?${params.toString()}`);
    } catch (err) {
      const status = err?.response?.status;
      const detailRaw =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.response?.data?.error;

      let msg;
      if (status === 409) {
        msg =
          typeof detailRaw === "string" && detailRaw
            ? detailRaw
            : "We already have a recent inquiry from this email. Please check your inbox for our confirmation, or reach out to customersupport@atlasforgefinancial.com.";
      } else if (status === 422) {
        msg =
          typeof detailRaw === "string" && detailRaw
            ? detailRaw
            : "One of the fields looks off. Please review and try again.";
      } else {
        msg =
          typeof detailRaw === "string" && detailRaw
            ? detailRaw
            : "We couldn't submit your inquiry right now. Please try again in a moment or email customersupport@atlasforgefinancial.com.";
      }
      toast.error(msg);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="Request a Quote | Safe to Spend 365 for Business"
        description="Tell us about your team and we'll send a Stripe invoice with your master code within one business day."
        path="/for-employers/inquiry"
      />

      <section className="relative border-b border-slate-100">
        <div className="mx-auto max-w-3xl px-6 lg:px-10 pt-12 pb-6">
          <Link
            to="/for-employers"
            data-testid="inquiry-back-link"
            className="inline-flex items-center gap-1.5 text-[13px] text-slate-500 hover:text-[#0A1128] transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to overview
          </Link>
        </div>
      </section>

      <section className="relative">
        <div className="mx-auto max-w-3xl px-6 lg:px-10 py-10 md:py-16">
          <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">
            Talk to sales
          </p>
          <h1
            data-testid="inquiry-title"
            className="mt-2 font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[#0A1128]"
          >
            Get a quote in one business day.
          </h1>
          <p className="mt-4 text-[15px] text-slate-600 max-w-2xl leading-relaxed">
            We'll send a Stripe invoice on approval and your master code the
            moment payment clears. No procurement dance, no seat-license quirks.
          </p>

          <form
            onSubmit={submit}
            data-testid="employer-inquiry-form"
            className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 md:p-8 space-y-5"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Company name" required>
                <input
                  type="text"
                  value={form.company_name}
                  onChange={(e) => update({ company_name: e.target.value })}
                  maxLength={120}
                  minLength={2}
                  required
                  data-testid="inquiry-company-name"
                  className="af-input"
                  placeholder="Acme Inc."
                />
              </Field>
              <Field label="Employee count" required>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={100000}
                  value={form.employee_count}
                  onChange={(e) => {
                    // Allow empty while typing so users can clear and re-enter.
                    const raw = e.target.value;
                    if (raw === "") {
                      update({ employee_count: "" });
                      return;
                    }
                    const digits = raw.replace(/\D/g, "");
                    if (!digits) {
                      update({ employee_count: "" });
                      return;
                    }
                    update({
                      employee_count: Math.min(100000, Number(digits)),
                    });
                  }}
                  onBlur={() => {
                    // On blur, snap empty / zero back to 1 so the form always
                    // submits a valid value and pricing keeps rendering.
                    const n = Number(form.employee_count);
                    if (!Number.isFinite(n) || n < 1) {
                      update({ employee_count: 1 });
                    }
                  }}
                  required
                  data-testid="inquiry-employee-count"
                  className="af-input"
                />
              </Field>
              <Field label="Your name" required>
                <input
                  type="text"
                  value={form.contact_name}
                  onChange={(e) => update({ contact_name: e.target.value })}
                  maxLength={80}
                  minLength={2}
                  required
                  data-testid="inquiry-contact-name"
                  className="af-input"
                  placeholder="Jane Doe"
                />
              </Field>
              <Field label="Work email" required>
                <input
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => update({ contact_email: e.target.value })}
                  required
                  data-testid="inquiry-contact-email"
                  className="af-input"
                  placeholder="jane@acme.com"
                />
              </Field>
              <Field label="Phone (optional)">
                <input
                  type="tel"
                  value={form.contact_phone}
                  onChange={(e) => update({ contact_phone: e.target.value })}
                  maxLength={30}
                  data-testid="inquiry-contact-phone"
                  className="af-input"
                  placeholder="+1 (555) 555-0100"
                />
              </Field>
              <Field label="Package">
                <select
                  value={form.package}
                  onChange={(e) => update({ package: e.target.value })}
                  data-testid="inquiry-package"
                  className="af-input"
                >
                  {packages.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                      {t.description ? ` — ${t.description}` : ""}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Anything we should know? (optional)">
              <textarea
                value={form.notes}
                onChange={(e) => update({ notes: e.target.value })}
                rows={3}
                maxLength={2000}
                data-testid="inquiry-notes"
                className="af-input resize-none"
                placeholder="Rollout timeline, HR system, integration needs, questions…"
              />
            </Field>
            {/* Honeypot — visually hidden but not display:none so bots still see it. */}
            <input
              type="text"
              value={form.website}
              onChange={(e) => update({ website: e.target.value })}
              tabIndex={-1}
              autoComplete="off"
              className="hidden"
              aria-hidden
            />
            <label className="flex items-start gap-2 text-[12.5px] text-slate-600">
              <input
                type="checkbox"
                checked={form.analytics_opt_in}
                onChange={(e) => update({ analytics_opt_in: e.target.checked })}
                data-testid="inquiry-analytics-opt-in"
                className="mt-0.5 h-4 w-4 accent-[#0A1128]"
              />
              <span>
                Allow AtlasForge to include anonymised program outcomes in
                aggregate benchmarks so other employers can see the impact. You
                can opt out anytime.
              </span>
            </label>
            {error && (
              <p
                data-testid="inquiry-error"
                className="text-[13px] text-rose-600"
              >
                {error}
              </p>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              <p className="text-[12px] text-slate-500 flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" />
                Your info is encrypted in transit and never sold.
              </p>
              <button
                type="submit"
                disabled={submitting}
                data-testid="inquiry-submit"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#0A1128] text-white px-5 py-2.5 text-[14px] font-medium hover:bg-[#1E293B] disabled:opacity-60 transition-colors"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Building2 className="h-4 w-4" />
                )}
                Request quote
              </button>
            </div>
          </form>

          {/* Live estimate footer */}
          {selectedPrice && !selectedPrice.is_custom && selectedPrice.total_display && (
            <div
              data-testid="inquiry-estimate"
              className="mt-4 text-center text-[13px] text-slate-600"
            >
              Estimated total for{" "}
              <strong className="text-[#0A1128]">
                {form.employee_count.toLocaleString()}
              </strong>{" "}
              employees on{" "}
              <strong className="text-[#0A1128]">
                {selected?.label || form.package}
              </strong>
              :{" "}
              <strong className="text-[#0A1128]">
                {selectedPrice.total_display}
              </strong>
              <span className="text-slate-400">
                {" "}
                · final invoice confirmed after approval
              </span>
              {loading && (
                <span className="text-slate-400"> · updating…</span>
              )}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[12px] uppercase tracking-wider text-slate-500 mb-1.5 font-semibold">
        {label}
        {required && <span className="text-rose-500 ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}
